import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from './auth-code-flow.config';
import { SymphonyAuthService } from './symphony-auth.service';
import { DashboardService } from '../services/dashboard/dashboard.service';
import { AuthPromptService } from './auth-prompt.service';
import { AuthModeService } from './auth-mode.service';
import { getPathFromBrowser, isErrorPath } from './browser-path.util';
import { ADK_LOADER, WINDOW } from '../app.tokens';
import { decodePowerBiDeepLinkState } from './powerbi-deeplink-state';

export interface AuthStartupHandlers {
  /**
   * Called once authentication has succeeded and the application should
   * proceed with its normal startup (e.g. dashboard + main UI load).
   */
  onAppReady: () => void;

  /**
   * Optional hook to update UI state while a login redirect is in progress.
   */
  setRedirectingToLogin?: (redirecting: boolean) => void;

  /**
   * Optional hook to signal that the ADK / auth preconditions are satisfied.
   * Historically this has been used to gate rendering of the main UI.
   */
  setAdkReady?: (ready: boolean) => void;
}

/**
 * Central orchestrator for authentication and initial app startup.
 *
 * Handles:
 * - CoT vs IdP branching (via AuthModeService)
 * - Route flag setup (logout / error)
 * - IdP discovery + token checks
 * - CoT ADK startup and JWT validation
 * - Calling DashboardService.onLoad() before handing control to the app
 */
@Injectable({ providedIn: 'root' })
export class AuthOrchestratorService {
  private isLogoutRoute = false;
  private isErrorRoute = false;

  constructor(
    private readonly router: Router,
    private readonly oauthService: OAuthService,
    private readonly symphonyAuthService: SymphonyAuthService,
    private readonly dashboardService: DashboardService,
    private readonly authPromptService: AuthPromptService,
    private readonly authModeService: AuthModeService,
    @Optional() @Inject(ADK_LOADER) private readonly adkLoader?: () => Promise<{ default?: { start: (config: any) => Promise<void> } }>,
    @Optional() @Inject(WINDOW) private readonly winRef?: Window & { location?: { search?: string } }
  ) {}

  /** SessionStorage key used to persist reportId across OAuth redirect (Power BI deep link). */
  private static readonly POWERBI_REPORT_ID_KEY = 'powerbi_reportId';
  /** SessionStorage key used to persist report parameters (rp:*) across OAuth redirect (Power BI deep link). */
  private static readonly POWERBI_REPORT_PARAMS_KEY = 'powerbi_reportParams';

  /**
   * Entry point used by AppComponent to kick off auth + app startup for
   * the current route.
   */
  bootstrapAppForCurrentRoute(handlers: AuthStartupHandlers): void {
    this.persistReportIdFromUrl();
    const persistedAfter = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY) : null;
    console.log('[PowerBI DeepLink] bootstrapAppForCurrentRoute: after persistReportIdFromUrl, powerbi_reportId in sessionStorage:', persistedAfter ?? '(empty)');
    this.runPostAuthSetup();

    const path = this.router.url.split('?')[0];
    const locationRef = (this.winRef !== undefined ? this.winRef?.location : (typeof globalThis !== 'undefined' && (globalThis as any).location)) || null;
    const hasCallbackCode = typeof locationRef?.search === 'string' && locationRef.search.includes('code=');
    console.log('[PowerBI DeepLink] bootstrapAppForCurrentRoute: path=', path, 'hasCallbackCode=', hasCallbackCode, 'router.url=', this.router.url);
    const pathFromBrowser = getPathFromBrowser();
    if (path === '/error' || isErrorPath(pathFromBrowser)) {
      return;
    }
    const isSilentRefresh = typeof locationRef?.search === 'string' && locationRef.search.includes('silent-refresh=true');
    if (isSilentRefresh) {
      return;
    }
    const isHomepage = path === '' || path === '/';
    const getLoginUserInfoFailed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('getLoginUserInfoFailed') === 'true';
    if (getLoginUserInfoFailed && !hasCallbackCode && !isHomepage) {
      return;
    }
    if (isHomepage && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('getLoginUserInfoFailed');
    }
    const isSpecialRoute = this.isLogoutRoute || this.isErrorRoute;

    if (isHomepage && !isSpecialRoute && this.authModeService.isCot()) {
      handlers.setRedirectingToLogin?.(true);
      this.authPromptService.promptLogin();
      return;
    }

    if (this.authModeService.isIdp()) {
      this.handleIdpStartup(handlers);
      return;
    }

    if (this.authModeService.isCot()) {
      this.handleCotStartup(handlers);
    }
  }

  private handleIdpStartup(handlers: AuthStartupHandlers): void {
    const pathForErrorCheck = (this.winRef?.location as { pathname?: string } | undefined)?.pathname ?? getPathFromBrowser();
    if (isErrorPath(pathForErrorCheck)) {
      return;
    }
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        if (this.oauthService.hasValidAccessToken()) {
          console.log('[PowerBI DeepLink] handleIdpStartup: hasValidAccessToken=true, oauthService.state=', this.oauthService.state ?? '(empty)');
          this.restorePowerBiDeepLinkFromOAuthState();
          const locationRef = (this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location);
          const hasCallbackCode = typeof locationRef?.search === 'string' && locationRef.search.includes('code=');
          if (typeof locationRef?.search === 'string' && locationRef.search.includes('silent-refresh=true')) {
            return;
          }
          const getLoginUserInfoFailed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('getLoginUserInfoFailed') === 'true';
          if (getLoginUserInfoFailed && !hasCallbackCode) {
            return;
          }
          if (hasCallbackCode && typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('getLoginUserInfoFailed');
          }
          this.oauthService.setupAutomaticSilentRefresh();
          handlers.setAdkReady?.(true);
          this.runAppStartup(handlers);
        } else {
          const loc = this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location;
          const hasCode = typeof loc?.search === 'string' && (loc as { search?: string }).search?.includes('code=');
          const persistedBeforeLogin = AuthOrchestratorService.getPersistedReportId();
          console.log('[PowerBI DeepLink] handleIdpStartup: hasValidAccessToken=false, hasCode=', hasCode, 'persistedReportId=', persistedBeforeLogin ?? '(empty)');
          if (!hasCode && !this.isOnErrorRoute()) {
            this.persistReportIdFromUrl();
            handlers.setRedirectingToLogin?.(true);
            this.authPromptService.promptLogin();
          }
        }
      })
      .catch((err) => {
        console.error('[AuthOrchestrator] loadDiscoveryDocumentAndTryLogin failed', err);
        const loc = this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location;
        const hasCode = typeof (loc as { search?: string })?.search === 'string' && (loc as { search?: string }).search?.includes('code=');
        if (!hasCode && !this.isOnErrorRoute()) {
          this.persistReportIdFromUrl();
          handlers.setRedirectingToLogin?.(true);
          this.authPromptService.promptLogin();
        }
      });
  }

  private handleCotStartup(handlers: AuthStartupHandlers): void {
    const win = this.winRef !== undefined ? this.winRef : (globalThis as any).window;
    const hasWindow = win != null;
    if (!hasWindow) {
      handlers.setAdkReady?.(true);
      this.runAppStartup(handlers);
      return;
    }
    const loadAdk = this.adkLoader ?? (() => import('@symphony-ui/adk'));
    loadAdk().then((adkModule) => this.startSymphonyAdk(adkModule, handlers));
  }

  private runAppStartup(handlers: AuthStartupHandlers): void {
    this.dashboardService.onLoad()
      .then(() => {
        const currentPath = this.router.url.split('?')[0];
        if (currentPath !== '/error' && currentPath !== '/logout') {
          handlers.onAppReady();
        }
      })
      .catch((err) => {
        console.error('[AuthOrchestrator] dashboardService.onLoad rejected', err);
        const currentHeading = this.dashboardService.errorMessageHeading.getValue();
        const currentMessage = this.dashboardService.errorMessage.getValue();
        if (!currentHeading || !currentMessage) {
          this.dashboardService.errorMessageHeading.next('Service Unavailable');
          this.dashboardService.errorMessage.next("We're currently experiencing some technical difficulties. Please try again later. If the issue persists, contact support at DC_QwynSupport.");
        }
      });
  }

  private runPostAuthSetup(): void {
    const win = this.winRef !== undefined ? this.winRef : (globalThis as any).window;
    if (win?.location?.search?.includes('silent-refresh=true')) {
      return;
    }
    const setRouteFlags = (url: string) => {
      const path = url.split('?')[0];
      this.isLogoutRoute = path === '/logout';
      this.isErrorRoute = path === '/error';
    };
    setRouteFlags(this.router.url);
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        setRouteFlags(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Expose route flags for components/templates that need to adjust layout
   * based on logout/error routes.
   */
  isLogoutRouteActive(): boolean {
    return this.isLogoutRoute;
  }

  isErrorRouteActive(): boolean {
    return this.isErrorRoute;
  }

  private isOnErrorRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/error' || isErrorPath(getPathFromBrowser());
  }

  /**
   * If the URL contains reportId (Power BI deep link), store it so it survives
   * an OAuth redirect. loadApplication() will read it from sessionStorage after callback.
   * Reads from both location.search and location.hash so it works when the app opens
   * in a new tab (e.g. from Power BI Web URL) with query in hash or search.
   */
  private persistReportIdFromUrl(): void {
    if (typeof sessionStorage === 'undefined') return;
    const locationRef = (this.winRef !== undefined ? this.winRef?.location : (typeof globalThis !== 'undefined' && (globalThis as any).location)) || null;
    const search = typeof locationRef?.search === 'string' ? locationRef.search : '';
    const hash = typeof locationRef?.hash === 'string' ? locationRef.hash : '';

    // Build a combined query string from search and any query portion inside hash
    let combinedQuery = search || '';
    if (hash && hash.includes('?')) {
      const hashQuery = hash.substring(hash.indexOf('?'));
      combinedQuery = combinedQuery ? `${combinedQuery}&${hashQuery.replace(/^[?#]/, '')}` : hashQuery;
    }

    if (!combinedQuery) {
      console.log('[PowerBI DeepLink] persistReportIdFromUrl: no combinedQuery, skip');
      return;
    }

    const params = new URLSearchParams(combinedQuery);

    // Persist reportId itself
    const reportId = params.get('reportId');
    if (reportId) {
      sessionStorage.setItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY, reportId.trim());
      console.log('[PowerBI DeepLink] persistReportIdFromUrl (Angular): set powerbi_reportId=', reportId.trim());
    } else {
      console.log('[PowerBI DeepLink] persistReportIdFromUrl (Angular): no reportId in URL');
    }

    // Persist any report parameters (rp:*) so they survive OAuth redirects
    const reportParams: Record<string, string | string[]> = {};
    for (const key of params.keys()) {
      if (!key.startsWith('rp:')) continue;
      const values = params.getAll(key);
      if (values.length === 1) {
        reportParams[key] = values[0];
      } else if (values.length > 1) {
        reportParams[key] = values;
      }
    }
    if (Object.keys(reportParams).length > 0) {
      try {
        sessionStorage.setItem(
          AuthOrchestratorService.POWERBI_REPORT_PARAMS_KEY,
          JSON.stringify(reportParams)
        );
      } catch {
        // Ignore JSON/storage errors – deep link will still work without params
      }
    }
  }

  /** Read persisted reportId without removing it (so MisReportComponent can still read it after loadApplication). */
  static getPersistedReportId(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    const id = sessionStorage.getItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY);
    return id?.trim() ?? null;
  }

  /** Read persisted report parameters (rp:*) without removing them. */
  static getPersistedReportParams(): Record<string, string | string[]> | null {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(AuthOrchestratorService.POWERBI_REPORT_PARAMS_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Read and remove persisted reportId (use only when you need to consume once). */
  static consumePersistedReportId(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    const id = sessionStorage.getItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY);
    if (id) {
      sessionStorage.removeItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY);
      return id.trim();
    }
    return null;
  }

  /** Read and remove persisted report parameters (rp:*). */
  static consumePersistedReportParams(): Record<string, string | string[]> | null {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(AuthOrchestratorService.POWERBI_REPORT_PARAMS_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AuthOrchestratorService.POWERBI_REPORT_PARAMS_KEY);
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * After OAuth callback, restore Power BI deep-link from state into sessionStorage
   * so loadApplication() shows the report even when the callback landed in a different tab/window.
   * Uses oauthService.state first; if empty (e.g. IdP base64-encodes state and library doesn't decode),
   * parses state param from the callback URL and extracts our payload (nonceStateSeparator ';').
   */
  private restorePowerBiDeepLinkFromOAuthState(): void {
    if (typeof sessionStorage === 'undefined') return;
    let payload = decodePowerBiDeepLinkState(this.oauthService.state);
    if (!payload?.reportId) {
      payload = this.getPowerBiPayloadFromCallbackUrl();
      if (payload) {
        console.log('[PowerBI DeepLink] restorePowerBiDeepLinkFromOAuthState: restored from callback URL state param');
      }
    }
    console.log('[PowerBI DeepLink] restorePowerBiDeepLinkFromOAuthState: oauthService.state=', this.oauthService.state ?? '(empty)', 'payload=', payload ? { reportId: payload.reportId } : null);
    if (!payload?.reportId) return;
    sessionStorage.setItem(AuthOrchestratorService.POWERBI_REPORT_ID_KEY, payload.reportId.trim());
    console.log('[PowerBI DeepLink] restorePowerBiDeepLinkFromOAuthState: set powerbi_reportId=', payload.reportId.trim());
    if (payload.reportParams && Object.keys(payload.reportParams).length > 0) {
      try {
        sessionStorage.setItem(
          AuthOrchestratorService.POWERBI_REPORT_PARAMS_KEY,
          JSON.stringify(payload.reportParams)
        );
      } catch {
        // ignore
      }
    }
  }

  /** Parse state param from callback URL when oauthService.state is empty (e.g. IdP base64-encodes state). */
  private getPowerBiPayloadFromCallbackUrl(): ReturnType<typeof decodePowerBiDeepLinkState> {
    const locationRef = (this.winRef !== undefined ? this.winRef?.location : (typeof globalThis !== 'undefined' && (globalThis as any).location)) || null;
    const search = typeof locationRef?.search === 'string' ? locationRef.search : '';
    if (!search || !search.includes('state=')) return null;
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    const stateParam = params.get('state');
    if (!stateParam) return null;
    // IdP may return state base64-encoded; library uses nonceStateSeparator ';'
    let fullState: string;
    try {
      fullState = atob(stateParam);
    } catch {
      fullState = stateParam;
    }
    const sep = ';';
    const idx = fullState.indexOf(sep);
    const userState = idx >= 0 ? fullState.substring(idx + sep.length) : fullState;
    try {
      return decodePowerBiDeepLinkState(decodeURIComponent(userState));
    } catch {
      return decodePowerBiDeepLinkState(userState);
    }
  }

  private startSymphonyAdk(adkModule: any, handlers: AuthStartupHandlers): void {
    const ADK = adkModule?.default ?? adkModule;
    const config = this.symphonyAuthService.getConfig();
    ADK.start(config).then(() => {
      handlers.setAdkReady?.(true);
      this.runAppStartup(handlers);
    }).catch((err: unknown) => {
      console.error('[AuthOrchestrator] ADK.start rejected', err);
    });
  }
}

